import { SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { focusRing } from "theme/colors";
import viewTicketTokens, {
  ticketPriorityTones,
  ticketStatusTones,
} from "./ViewTicket.tokens";

const t = viewTicketTokens;

/** Chrome shared by the description, comments and details cards. */
export const cardSx: SxProps<Theme> = {
  bgcolor: t.color.surface,
  border: `1px solid ${t.color.cardBorder}`,
  borderRadius: t.radius.card,
  boxShadow: t.shadow.card,
  p: t.layout.cardPadding,
  boxSizing: "border-box",
};

export const cardTitleSx: SxProps<Theme> = {
  mb: t.layout.cardTitleMarginBottom,
  color: t.color.ink,
  fontSize: t.font.cardTitle,
  fontWeight: t.weight.heading,
  letterSpacing: t.letterSpacing.title,
  lineHeight: "normal",
};

/* ── Header ──────────────────────────────────────────────────────────────── */

/** "← Back to tickets". A button so it stays keyboard reachable. */
export const backLinkSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: t.header.backGap,
  p: 0,
  minWidth: 0,
  minHeight: 0,
  mb: t.header.backMarginBottom,
  color: t.color.ink,
  fontSize: t.font.backLink,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  textTransform: "none",
  "&:hover": { bgcolor: "transparent", color: t.color.accent },
};

export const headerSx: SxProps<Theme> = {
  mb: t.header.marginBottom,
};

export const titleRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: t.header.titleGap,
};

export const titleSx: SxProps<Theme> = {
  m: 0,
  color: t.color.ink,
  fontSize: t.font.title,
  fontWeight: t.weight.heading,
  letterSpacing: t.letterSpacing.title,
  lineHeight: 1.25,
};

export const metaSx: SxProps<Theme> = {
  mt: t.header.metaMarginTop,
  color: t.color.subtle,
  fontSize: t.font.meta,
  lineHeight: "normal",
};

/* ── Pills ───────────────────────────────────────────────────────────────── */

const pillBaseSx = {
  display: "inline-flex",
  alignItems: "center",
  justifySelf: "start",
  maxWidth: "100%",
  px: "11px",
  py: "3px",
  borderRadius: t.radius.pill,
  fontSize: t.font.statusPill,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  whiteSpace: "nowrap",
} as const;

export const statusPillSx = (status?: string): SxProps<Theme> => {
  const tone = ticketStatusTones[status ?? ""];
  return {
    ...pillBaseSx,
    bgcolor: tone?.bg ?? t.color.pillBg,
    color: tone?.color ?? t.color.pillText,
  };
};

export const priorityPillSx = (priority?: string): SxProps<Theme> => {
  const tone = ticketPriorityTones[priority ?? ""];
  return {
    ...pillBaseSx,
    bgcolor: tone?.bg ?? t.color.pillBg,
    color: tone?.color ?? t.color.pillText,
  };
};

/** Neutral pill — "Unassigned", and each named assignee. */
export const neutralPillSx: SxProps<Theme> = {
  ...pillBaseSx,
  bgcolor: t.color.pillBg,
  color: t.color.pillText,
};

/* ── Body layout ─────────────────────────────────────────────────────────── */

export const bodySx: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: "flex-start",
  gap: t.layout.columnGap,
};

export const mainColumnSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.layout.cardGap,
  flex: 1,
  minWidth: 0,
  width: "100%",
};

export const railSx: SxProps<Theme> = {
  width: { xs: "100%", md: t.layout.railWidth },
  flexShrink: 0,
};

/* ── Description card ────────────────────────────────────────────────────── */

export const descriptionBodySx: SxProps<Theme> = {
  color: t.color.body,
  fontSize: t.font.body,
  lineHeight: t.description.lineHeight,
  wordBreak: "break-word",
};

/** "ATTACHMENTS" strip above the chip row. */
export const sectionLabelSx: SxProps<Theme> = {
  display: "block",
  mt: t.description.labelMarginTop,
  mb: t.description.labelMarginBottom,
  color: t.color.subtle,
  fontSize: t.font.sectionLabel,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.sectionLabel,
  lineHeight: "normal",
};

export const attachmentRowSx: SystemStyleObject<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: t.description.chipGap,
};

/** File chip: glyph, name, then the download affordance. */
export const attachmentChipSx = (clickable: boolean): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  gap: t.description.chipInnerGap,
  maxWidth: "100%",
  p: t.description.chipPadding,
  bgcolor: t.color.surface,
  border: `1px solid ${t.color.cardBorder}`,
  borderRadius: t.radius.control,
  cursor: clickable ? "pointer" : "default",
  "&:hover": clickable ? { borderColor: t.color.ink } : undefined,
});

export const attachmentIconSx: SxProps<Theme> = {
  width: t.description.chipIconSize,
  height: t.description.chipIconSize,
  color: t.color.subtle,
  flexShrink: 0,
};

/**
 * The chip's file name. Rendered as a bare button when the file has a preview,
 * so it stays keyboard reachable — `font: inherit` lands first because buttons
 * do not inherit the page font and the shorthand would reset the steps below.
 */
export const attachmentNameSx = (clickable: boolean): SxProps<Theme> => ({
  font: "inherit",
  p: 0,
  m: 0,
  border: "none",
  bgcolor: "transparent",
  minWidth: 0,
  textAlign: "left",
  color: t.color.ink,
  fontSize: t.font.attachment,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  cursor: clickable ? "pointer" : "default",
});

export const attachmentDownloadSx: SxProps<Theme> = {
  width: t.description.chipIconSize + 8,
  height: t.description.chipIconSize + 8,
  p: 0,
  flexShrink: 0,
  color: t.color.accent,
  "&:hover": { bgcolor: t.color.toolbarHoverBg },
};

/* ── Comments card ───────────────────────────────────────────────────────── */

export const commentRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: t.comments.avatarGap,
};

export const avatarSx = (isSelf: boolean): SxProps<Theme> => ({
  width: t.comments.avatarSize,
  height: t.comments.avatarSize,
  flexShrink: 0,
  bgcolor: isSelf ? t.color.selfAvatarBg : t.color.avatarBg,
  color: isSelf ? t.color.selfAvatarText : t.color.avatarText,
  fontSize: t.font.avatar,
  fontWeight: t.weight.strong,
  letterSpacing: 0,
});

/** Collapsed "Add a comment" field. */
export const commentPlaceholderSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  p: t.comments.placeholderPadding,
  bgcolor: t.color.fieldBg,
  border: `1px solid ${t.color.fieldBorder}`,
  borderRadius: t.radius.control,
  color: t.color.placeholder,
  fontSize: t.font.body,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
  textAlign: "left",
  textTransform: "none",
  justifyContent: "flex-start",
  cursor: "pointer",
  "&:hover": { bgcolor: t.color.fieldBg, borderColor: t.color.ink },
};

export const threadSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.comments.threadGap,
  mt: t.comments.threadMarginTop,
};

/** Nested replies stack under their parent, indented past the avatar column. */
export const replyThreadSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.comments.threadGap,
  mt: t.comments.threadGap,
  pl: t.comments.replyIndent,
};

export const commentAuthorRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: t.comments.authorGap,
};

export const commentAuthorSx: SxProps<Theme> = {
  color: t.color.ink,
  fontSize: t.font.commentAuthor,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
};

export const commentTimestampSx: SxProps<Theme> = {
  color: t.color.subtle,
  fontSize: t.font.timestamp,
  lineHeight: "normal",
};

export const commentBodySx: SxProps<Theme> = {
  mt: t.comments.bodyMarginTop,
  color: t.color.body,
  fontSize: t.font.body,
  lineHeight: t.comments.bodyLineHeight,
  wordBreak: "break-word",
};

export const commentAttachmentRowSx: SxProps<Theme> = {
  ...attachmentRowSx,
  mt: t.comments.attachmentMarginTop,
};

/** Accent "Reply" affordance under a comment. */
export const replyLinkSx: SxProps<Theme> = {
  display: "inline-flex",
  p: 0,
  mt: t.comments.replyLinkMarginTop,
  minWidth: 0,
  minHeight: 0,
  color: t.color.accent,
  fontSize: t.font.replyLink,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  textTransform: "none",
  "&:hover": { bgcolor: "transparent", color: t.color.accentHover },
};

export const inlineEditorSx: SxProps<Theme> = {
  mt: t.comments.editorMarginTop,
};

export const noCommentsSx: SxProps<Theme> = {
  py: t.comments.emptyPaddingY,
  color: t.color.subtle,
  fontSize: t.font.rowValue,
  lineHeight: "normal",
};

/* ── Details rail ────────────────────────────────────────────────────────── */

export const detailsRowSx = (isLast: boolean): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: t.detailsRow.gap,
  py: t.detailsRow.paddingY,
  borderBottom: isLast ? "none" : `1px solid ${t.color.divider}`,
});

export const detailsLabelSx: SxProps<Theme> = {
  flexShrink: 0,
  color: t.color.muted,
  fontSize: t.font.rowLabel,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
};

export const detailsValueSx: SxProps<Theme> = {
  minWidth: 0,
  color: t.color.ink,
  fontSize: t.font.rowValue,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  textAlign: "right",
  wordBreak: "break-word",
};

/** Wraps one or more pills on the right-hand side of a row. */
export const detailsPillsSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  gap: "6px",
  minWidth: 0,
};

/* ── Comment editor ──────────────────────────────────────────────────────── */

/**
 * Bordered box holding the Quill toolbar, the text area and the footer. Quill's
 * snow theme draws its own borders around both halves; they are stripped here so
 * the shell provides the single outline the design asks for.
 */
export const editorShellSx: SxProps<Theme> = {
  bgcolor: t.color.surface,
  border: `1px solid ${t.color.fieldBorder}`,
  borderRadius: t.radius.editor,
  overflow: "hidden",

  "& .ql-toolbar.ql-snow": {
    border: "none",
    borderBottom: `1px solid ${t.color.hairline}`,
    bgcolor: t.color.fieldBg,
    p: t.editor.toolbarPadding,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    fontFamily: "inherit",
  },
  // Quill groups its buttons into `.ql-formats`; the design rules the groups
  // off from one another rather than relying on whitespace alone.
  "& .ql-toolbar.ql-snow .ql-formats": {
    display: "inline-flex",
    alignItems: "center",
    m: 0,
    "&:not(:last-child)": {
      pr: t.editor.toolbarGroupGap,
      mr: t.editor.toolbarGroupGap,
      borderRight: `1px solid ${t.color.hairline}`,
    },
  },
  "& .ql-toolbar.ql-snow button": {
    width: t.editor.buttonSize,
    height: t.editor.buttonSize,
    p: 0,
    borderRadius: t.radius.toolbarButton,
    color: t.color.muted,
    "&:hover": { bgcolor: t.color.toolbarHoverBg },
  },
  "& .ql-toolbar.ql-snow button svg": {
    width: t.editor.buttonIconSize,
    height: t.editor.buttonIconSize,
  },
  // Quill paints its glyphs through explicit stroke / fill attributes, so the
  // active and hover states have to be recoloured on those rather than `color`.
  "& .ql-snow .ql-stroke": { stroke: "currentColor" },
  "& .ql-snow .ql-fill": { fill: "currentColor" },
  "& .ql-snow .ql-thin": { stroke: "currentColor" },
  "& .ql-toolbar.ql-snow button.ql-active, & .ql-toolbar.ql-snow button:hover":
    {
      color: t.color.accent,
    },

  "& .ql-container.ql-snow": {
    border: "none",
    fontFamily: "inherit",
    fontSize: t.font.body,
  },
  "& .ql-editor": {
    minHeight: t.editor.minHeight,
    p: t.editor.contentPadding,
    color: t.color.ink,
    fontSize: t.font.body,
    lineHeight: t.comments.bodyLineHeight,
  },
  "& .ql-editor.ql-blank::before": {
    left: t.editor.contentPadding.split(" ")[1],
    right: t.editor.contentPadding.split(" ")[1],
    color: t.color.placeholder,
    fontStyle: "normal",
    fontSize: t.font.body,
  },
};

/** Attach hint on the left, Save / Cancel on the right. */
export const editorFooterSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: t.editor.footerGap,
  p: t.editor.footerPadding,
  borderTop: `1px solid ${t.color.hairline}`,
};

/** Paperclip plus its caption — the whole thing is the file input's label. */
export const attachTriggerSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: t.editor.hintGap,
  p: 0,
  minWidth: 0,
  minHeight: 0,
  color: t.color.subtle,
  fontSize: t.font.editorHint,
  fontWeight: t.weight.regular,
  lineHeight: "normal",
  textTransform: "none",
  "&:hover": { bgcolor: "transparent", color: t.color.ink },
  // The only tab stop is the visually-hidden file <input>; surface its focus on
  // the visible label so keyboard users see it (WCAG 2.4.7 Focus Visible).
  "&:focus-within": {
    outline: `2px solid ${focusRing}`,
    outlineOffset: "2px",
  },
};

export const attachIconSx: SxProps<Theme> = {
  width: t.editor.hintIconSize,
  height: t.editor.hintIconSize,
  flexShrink: 0,
};

export const editorActionsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.editor.footerGap,
  flexShrink: 0,
};

const editorButtonBaseSx = {
  height: t.editor.buttonHeight,
  minHeight: t.editor.buttonHeight,
  px: t.editor.buttonPaddingX,
  py: 0,
  borderRadius: t.radius.control,
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
} as const;

/**
 * `MuiButton.contained` paints with the `background` shorthand, which emotion
 * emits after an `sx` longhand and would reset the accent — so the fill is
 * pinned one specificity step higher, exactly as the list's action does.
 */
export const saveButtonSx: SxProps<Theme> = {
  ...editorButtonBaseSx,
  color: t.color.onAccent,
  border: "none",
  "&.MuiButton-root": {
    background: t.color.ctaBg,
    "&:hover": { background: t.color.ctaBgHover, boxShadow: "none" },
    "&.Mui-disabled": {
      background: t.color.ctaBg,
      opacity: 0.5,
      color: t.color.onAccent,
    },
  },
};

export const cancelButtonSx: SxProps<Theme> = {
  ...editorButtonBaseSx,
  "&.MuiButton-root": {
    background: t.color.surface,
    border: `1px solid ${t.color.fieldBorder}`,
    color: t.color.body,
    "&:hover": {
      background: t.color.surface,
      borderColor: t.color.ink,
      color: t.color.ink,
      boxShadow: "none",
    },
  },
};

/** File chips for attachments staged on an unsent comment. */
export const stagedFilesSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  px: t.editor.footerPadding.split(" ")[1],
  pb: t.editor.footerPadding.split(" ")[0],
};
